import React from 'react';
import type { InventoryItem } from '../../../types/inventory';
import { AutocompleteSelect, type AutocompleteOption } from '../../../components/common/AutocompleteSelect';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../utils/date';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastFormHeaderSectionProps {
  mode: 'roast' | 'recipe';
  recipeName: string;
  onRecipeNameChange: (value: string) => void;
  machine: string;
  onMachineChange: (value: string, option?: AutocompleteOption) => void;
  machineOptions: AutocompleteOption[];
  date: string;
  onDateChange: (value: string) => void;
  time: string;
  onTimeChange: (value: string) => void;
  roaster: string;
  onRoasterChange: (value: string) => void;
  beans: InventoryItem[];
  selectedBean: string;
  onBeanChange: (value: string) => void;
  readOnly?: boolean;
}

export const RoastFormHeaderSection: React.FC<RoastFormHeaderSectionProps> = React.memo(({
  mode,
  recipeName,
  onRecipeNameChange,
  machine,
  onMachineChange,
  machineOptions,
  date,
  onDateChange,
  time,
  onTimeChange,
  roaster,
  onRoasterChange,
  beans,
  selectedBean,
  onBeanChange,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const inputClassName = "w-full border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-200 hover:border-gray-400 bg-white";
  const disabledInputClassName = "w-full border border-gray-200 rounded-lg py-2.5 px-3.5 text-sm shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed";
  const labelClassName = "block text-sm font-medium text-gray-700 mb-1.5";

  if (mode === 'recipe') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className={labelClassName}>{t('roast.formHeader.profileName')}</label>
          <input
            type="text"
            value={recipeName}
            onChange={event => onRecipeNameChange(event.target.value)}
            disabled={readOnly}
            className={readOnly ? disabledInputClassName : inputClassName}
            placeholder={t('roast.formHeader.enterProfileName')}
          />
        </div>

        <div>
          <AutocompleteSelect
            label={t('roast.formHeader.machine')}
            dataTestId="recipe-machine-select"
            value={machine}
            onChange={onMachineChange}
            options={machineOptions}
            placeholder={t('roast.formHeader.selectMachineType')}
            allowCustom={false}
            disabled={readOnly}
          />
        </div>

        <div>
          <label className={labelClassName}>{t('roast.formHeader.beanItem')}</label>
          <select
            value={selectedBean}
            onChange={event => onBeanChange(event.target.value)}
            disabled={readOnly}
            className={readOnly ? disabledInputClassName : inputClassName}
          >
            <option value="">{t('roast.formHeader.selectBean')}</option>
            {beans.map(bean => (
              <option key={bean.id} value={bean.id}>
                {bean.name} ({bean.code})
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
      <div className="space-y-5">
        <div>
          <label className={labelClassName}>{t('roast.formHeader.roastNumber')}</label>
          <input disabled placeholder={t('forms.common.autoGenerated')} className={disabledInputClassName} />
        </div>

        <div>
          <label className={labelClassName}>{t('roast.formHeader.recipeName')}</label>
          <input
            type="text"
            value={recipeName}
            onChange={event => onRecipeNameChange(event.target.value)}
            disabled={readOnly}
            className={readOnly ? disabledInputClassName : inputClassName}
            placeholder={t('roast.formHeader.enterRecipeName')}
          />
        </div>

        <div>
          <AutocompleteSelect
            label={t('roast.formHeader.machine')}
            dataTestId="roast-machine-select"
            value={machine}
            onChange={onMachineChange}
            options={machineOptions}
            placeholder={t('roast.formHeader.selectMachineType')}
            allowCustom={false}
            disabled={readOnly}
          />
        </div>

        <div>
          <label className={labelClassName}>{t('roast.formHeader.beanItem')}</label>
          <select
            value={selectedBean}
            onChange={event => onBeanChange(event.target.value)}
            disabled={readOnly}
            className={readOnly ? disabledInputClassName : inputClassName}
          >
            <option value="">{t('roast.formHeader.selectBean')}</option>
            {beans.map(bean => (
              <option key={bean.id} value={bean.id}>
                {bean.name} ({bean.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className={labelClassName}>{t('reports.general.table.date')}</label>
          <input
            type="date"
            value={date}
            onChange={event => onDateChange(event.target.value)}
            onClick={openNativeDatePickerSafely}
            onKeyDown={preventManualDateEntry}
            onPaste={preventManualDatePasteOrDrop}
            onDrop={preventManualDatePasteOrDrop}
            disabled={readOnly}
            className={readOnly ? disabledInputClassName : inputClassName}
          />
        </div>

        <div>
          <label className={labelClassName}>{t('reports.shift.time')}</label>
          <input
            type="time"
            value={time}
            onChange={event => onTimeChange(event.target.value)}
            disabled={readOnly}
            className={readOnly ? disabledInputClassName : inputClassName}
          />
        </div>

        <div>
          <label className={labelClassName}>{t('roast.formHeader.roaster')}</label>
          <input
            type="text"
            value={roaster}
            onChange={event => onRoasterChange(event.target.value)}
            disabled={readOnly}
            className={readOnly ? disabledInputClassName : inputClassName}
            placeholder={t('roast.formHeader.enterRoasterName')}
          />
        </div>
      </div>
    </div>
  );
});
